import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lightbulb, Bug, X, Image as ImageIcon, Send, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_FILE_BYTES = 8 * 1024 * 1024;

// Inline modal — minimal styling, dark backdrop, white card, close on overlay click.
const Modal = ({ open, onClose, title, color, children }) => {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100000,
        padding: "1rem",
      }}
      data-testid="feedback-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          maxWidth: 520,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1.25rem 1.5rem",
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <h3 style={{ margin: 0, color, fontSize: "1.15rem", fontWeight: 700 }}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('feedback.close')}
            data-testid="feedback-modal-close"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#64748b",
              padding: 4,
              display: "flex",
            }}
          >
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: "1.25rem 1.5rem 1.5rem" }}>{children}</div>
      </div>
    </div>
  );
};

const FeedbackButtons = () => {
  const { user, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);
  const [ideaText, setIdeaText] = useState("");
  const [bugText, setBugText] = useState("");
  const [bugFile, setBugFile] = useState(null);
  const [sending, setSending] = useState(false);

  const canSubmitIdea = !!(user && user.email_verified);

  const submitIdea = async () => {
    if (!canSubmitIdea) return;
    const text = ideaText.trim();
    if (text.length < 5) {
      toast.error(t('feedback.describeIdeaMoreDetail'));
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/feedback/idea`,
        { text },
        { headers: { ...getAuthHeader() } }
      );
      toast.success(res.data?.message || "Спасибо! Идея отправлена.");
      setIdeaText("");
      setIdeaOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось отправить идею.");
    } finally {
      setSending(false);
    }
  };

  const submitBug = async () => {
    const text = bugText.trim();
    if (text.length < 5) {
      toast.error(t('feedback.describeProblemMoreDetail'));
      return;
    }
    if (bugFile && bugFile.size > MAX_FILE_BYTES) {
      toast.error(`Скриншот больше ${MAX_FILE_BYTES / (1024 * 1024)} МБ.`);
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("text", text);
      if (bugFile) fd.append("screenshot", bugFile);
      const headers = { "Content-Type": "multipart/form-data", ...getAuthHeader() };
      const res = await axios.post(`${API}/feedback/bug`, fd, { headers });
      toast.success(res.data?.message || "Спасибо! Сообщение отправлено.");
      setBugText("");
      setBugFile(null);
      setBugOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось отправить сообщение.");
    } finally {
      setSending(false);
    }
  };

  const btnStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.45rem",
    padding: "0.4rem 0.85rem",
    background: "transparent",
    color: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(255,255,255,0.35)",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: 500,
    transition: "background 0.15s, color 0.15s",
  };

  const ideaUnavailableMsg =
    "Делиться идеями могут только зарегистрированные игроки с подтверждённой почтой. Зарегистрируйтесь и подтвердите email, чтобы предложить идею.";

  const sendBtnStyle = (color) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "0.4rem",
    padding: "0.55rem 1.1rem",
    background: color,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: sending ? "not-allowed" : "pointer",
    fontWeight: 600,
    fontSize: "0.95rem",
    opacity: sending ? 0.7 : 1,
  });

  const textareaStyle = {
    width: "100%",
    minHeight: 130,
    padding: "0.75rem",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontFamily: "inherit",
    fontSize: "0.95rem",
    color: "#1e293b",
    background: "#fff",
    resize: "vertical",
    boxSizing: "border-box",
  };

  return (
    <>
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "center" }}>
        <button
          type="button"
          onClick={() => setIdeaOpen(true)}
          style={btnStyle}
          data-testid="footer-share-idea-btn"
        >
          <Lightbulb size={16} />
          {t('feedback.shareIdea')}
        </button>
        <button
          type="button"
          onClick={() => setBugOpen(true)}
          style={btnStyle}
          data-testid="footer-report-bug-btn"
        >
          <Bug size={16} />
          {t('feedback.reportBug')}
        </button>
      </div>

      {/* IDEA */}
      <Modal open={ideaOpen} onClose={() => !sending && setIdeaOpen(false)} title={t('feedback.shareIdea2')} color="#667eea">
        {!canSubmitIdea ? (
          <div
            style={{
              padding: "1rem 1.1rem",
              background: "#fef3c7",
              border: "1px solid #fde68a",
              borderRadius: 10,
              color: "#92400e",
              lineHeight: 1.5,
            }}
            data-testid="idea-locked-message"
          >
            {ideaUnavailableMsg}
          </div>
        ) : (
          <>
            <p style={{ margin: "0 0 0.6rem 0", color: "#64748b", fontSize: "0.9rem" }}>
              {t('feedback.whatWouldLikeSee')}
            </p>
            <textarea
              value={ideaText}
              onChange={(e) => setIdeaText(e.target.value)}
              maxLength={5000}
              placeholder={t('feedback.exampleAddTeamGame')}
              style={textareaStyle}
              disabled={sending}
              data-testid="idea-text-input"
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem", gap: "0.6rem" }}>
              <button
                type="button"
                onClick={submitIdea}
                disabled={sending}
                style={sendBtnStyle("#667eea")}
                data-testid="idea-submit-btn"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Отправить
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* BUG */}
      <Modal open={bugOpen} onClose={() => !sending && setBugOpen(false)} title={t('feedback.reportBug2')} color="#dc2626">
        <p style={{ margin: "0 0 0.6rem 0", color: "#64748b", fontSize: "0.9rem" }}>
          {t('feedback.somethingNotWorkingExpected')}
        </p>
        <textarea
          value={bugText}
          onChange={(e) => setBugText(e.target.value)}
          maxLength={5000}
          placeholder={t('feedback.exampleWhenUsingTimecode')}
          style={textareaStyle}
          disabled={sending}
          data-testid="bug-text-input"
        />

        <label
          htmlFor="bug-screenshot-input"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            marginTop: "0.85rem",
            padding: "0.6rem 0.85rem",
            border: "1px dashed #cbd5e1",
            borderRadius: 8,
            cursor: "pointer",
            color: "#475569",
            background: "#f8fafc",
            fontSize: "0.9rem",
          }}
          data-testid="bug-screenshot-label"
        >
          <ImageIcon size={18} />
          {bugFile ? (
            <span style={{ color: "#1e293b", fontWeight: 500 }}>
              {bugFile.name} ({Math.round(bugFile.size / 1024)} КБ)
            </span>
          ) : (
            <span>{t('feedback.attachScreenshotOptional')}</span>
          )}
        </label>
        <input
          id="bug-screenshot-input"
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
          onChange={(e) => setBugFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
          style={{ display: "none" }}
          disabled={sending}
          data-testid="bug-screenshot-input"
        />
        {bugFile && (
          <button
            type="button"
            onClick={() => setBugFile(null)}
            style={{
              marginTop: 6,
              background: "transparent",
              border: "none",
              color: "#dc2626",
              cursor: "pointer",
              fontSize: "0.85rem",
            }}
            disabled={sending}
            data-testid="bug-screenshot-remove"
          >
            {t('feedback.removeScreenshot')}
          </button>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem", gap: "0.6rem" }}>
          <button
            type="button"
            onClick={submitBug}
            disabled={sending}
            style={sendBtnStyle("#dc2626")}
            data-testid="bug-submit-btn"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Отправить
          </button>
        </div>
      </Modal>
    </>
  );
};

export default FeedbackButtons;
